import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import {
  Gift, HeartPulse, ArrowRight, Star, Ticket, Pill, Cake, ShieldCheck,
  UserPlus, TrendingUp, ShoppingCart, Film, Utensils, Smartphone, Shirt, Check,
  Crown, Sparkles,
} from "lucide-react";

const HERO = "https://images.pexels.com/photos/3875141/pexels-photo-3875141.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const RAFFLE_IMG = "https://images.pexels.com/photos/17134085/pexels-photo-17134085.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const steps = [
  { icon: UserPlus, n: "1", title: "Regístrate", desc: "Solo necesitas ser cliente activo de Armony con débito automático o tarjeta de crédito." },
  { icon: TrendingUp, n: "2", title: "Acumula puntos", desc: "Paga a tiempo, agrega beneficiarios y participa en las dinámicas del club." },
  { icon: Gift, n: "3", title: "Canjea y gana", desc: "Gift cards, descuentos, mensualidades gratis y el gran sorteo de diciembre." },
];

const benefits = [
  { icon: Pill, title: "Descuentos en farmacia", desc: "Ahorra en tu farmacia aliada desde el primer día." },
  { icon: Cake, title: "Puntos de cumpleaños", desc: "Recibe puntos de regalo en tu mes de cumpleaños." },
  { icon: Gift, title: "Gift cards y canjes", desc: "Súper, cine, restaurantes, ropa y recargas de celular." },
  { icon: HeartPulse, title: "Salud y mascota", desc: "Telemedicina, servicios médicos y orientación veterinaria." },
];

const tiers = [
  { name: "Bronce", pts: "0 pts", grad: "from-[#8A5622] to-[#CD7F32]", tagline: "El punto de partida. Beneficios desde el primer día.", perks: ["Descuento en farmacia aliada", "+10 puntos en tu cumpleaños", "Servicios médicos gratis (1 mes)", "Cuidado de mascota (1 mes)"] },
  { name: "Plata", pts: "1,000 pts", grad: "from-[#6B7280] to-[#9CA3AF]", tagline: "Tu constancia tiene recompensa.", perks: ["Todo lo de Bronce", "Contenido digital exclusivo", "Repatriación 50% off (6 meses)", "Servicios médicos gratis (2 meses)"] },
  { name: "Oro", pts: "5,000 pts", grad: "from-[#B8860B] via-[#E0A82E] to-[#F6D77A]", tagline: "Atención personalizada y acceso prioritario.", perks: ["Todo lo de Plata", "Plan grupal 50% off (1 año)", "Repatriación 50% off (1 año)", "Servicios médicos gratis (3 meses)"] },
  { name: "VIP", pts: "15,000 pts", grad: "from-[#123884] to-[#123884]", tagline: "La experiencia más completa y exclusiva.", perks: ["Todo lo de Oro", "Línea directa exclusiva", "1 beneficiario gratis al año", "Cuidado de mascota (3 meses)"] },
];

const giftcards = [
  { icon: ShoppingCart, label: "Supermercados" },
  { icon: Utensils, label: "Restaurantes" },
  { icon: Film, label: "Cine" },
  { icon: Shirt, label: "Tiendas de ropa" },
  { icon: Smartphone, label: "Recargas celular" },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<any[]>([]);
  const [billing, setBilling] = useState<"month" | "year">("month");

  useEffect(() => {
    if (user) navigate(user.role === "customer" ? "/portal" : "/admin");
  }, [user, navigate]);

  useEffect(() => {
    api.get("/plans/public").then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-warm-bg text-slate-800">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 bg-navy/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link to="/" data-testid="landing-logo" className="flex items-center shrink-0">
            <BrandLogo bare tag="Contigo" />
          </Link>
          <nav className="flex items-center gap-1.5 sm:gap-3">
            <a href="#niveles" className="hidden md:block text-sm text-white/80 hover:text-white transition-colors px-3">Niveles</a>
            <a href="#planes" className="hidden md:block text-sm text-white/80 hover:text-white transition-colors px-3">Planes</a>
            <a href="#sorteo" className="hidden md:block text-sm text-white/80 hover:text-white transition-colors px-3">Sorteo</a>
            <Button data-testid="nav-login-link" variant="ghost" onClick={() => navigate("/login")} className="hidden sm:inline-flex text-white hover:bg-white/10">
              Iniciar sesión
            </Button>
            <Button data-testid="landing-register-button" onClick={() => navigate("/registro")} className="bg-gold hover:bg-gold-light text-navy-dark font-bold rounded-full px-4 sm:px-5">
              Registrarme
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center pt-16 overflow-hidden bg-navy">
        <div className="absolute inset-0">
          <img src={HERO} alt="Familia Armony Contigo" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy/85" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-dark/60 via-navy/80 to-navy" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white pb-40 sm:pb-44 animate-fade-up">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/90 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 mb-6">
            <ShieldCheck className="w-4 h-4 text-gold" /> 25+ años cuidando a las familias del Ecuador
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold leading-tight">
            Tu lealtad tiene recompensa con Armony Contigo
          </h1>
          <p className="mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl mx-auto">
            Convierte el pago puntual de tu plan en premios reales: equipa tu hogar, ahorra en farmacias y accede a salud para tu familia. Acumula puntos, sube de nivel y gana.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Button data-testid="hero-register-button" onClick={() => navigate("/registro")} className="bg-gold hover:bg-gold-light text-navy-dark font-bold h-12 px-8 rounded-full text-base">
              Registrarme ahora <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button data-testid="hero-benefits-button" variant="outline" onClick={() => navigate("/login")} className="h-12 px-8 rounded-full text-base border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white">
              Ya soy miembro
            </Button>
          </div>
        </div>
        {/* Stats card overlay */}
        <div className="absolute z-10 bottom-8 inset-x-0 px-6">
          <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-4 sm:px-8 py-6 grid grid-cols-3 gap-2 sm:gap-6 text-center text-white">
            <div>
              <p className="text-2xl sm:text-3xl font-serif font-bold">+25 años</p>
              <p className="text-[11px] sm:text-sm text-white/70 mt-1">De trayectoria y experiencia</p>
            </div>
            <div className="border-x border-white/20 px-1">
              <p className="text-2xl sm:text-3xl font-serif font-bold">+130.000</p>
              <p className="text-[11px] sm:text-sm text-white/70 mt-1">Clientes confían en nosotros</p>
            </div>
            <div>
              <p className="text-2xl sm:text-3xl font-serif font-bold">24/7</p>
              <p className="text-[11px] sm:text-sm text-white/70 mt-1">Atención permanente</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="como-funciona" className="max-w-7xl mx-auto px-6 py-16 scroll-mt-16">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.2em] text-gold-ink font-semibold">Cómo funciona</span>
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy mt-3">Tres pasos para empezar a ganar</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="card-obsidian p-8 relative">
              <span aria-hidden className="absolute top-6 right-6 text-5xl font-serif font-bold text-slate-200">{s.n}</span>
              <div className="w-12 h-12 rounded-xl gold-gradient-bg flex items-center justify-center mb-5">
                <s.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-slate-900 mb-2">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Immediate benefits */}
      <section id="beneficios" className="bg-secondary/60 py-20 scroll-mt-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.2em] text-gold-ink font-semibold">Beneficios inmediatos</span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy mt-3">Desde el día en que te registras</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center mb-5">
                  <b.icon className="w-6 h-6 text-gold-ink" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section id="niveles" className="max-w-7xl mx-auto px-6 py-20 scroll-mt-16">
        <div className="text-center mb-4">
          <span className="text-xs uppercase tracking-[0.2em] text-gold-ink font-semibold">Mientras más leal eres, más ganas</span>
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy mt-3">Tu nivel crece contigo</h2>
          <p className="text-sm text-slate-500 mt-2">Tu nivel se actualiza cada 3 meses según tu comportamiento como cliente.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {tiers.map((t, i) => (
            <div key={i} className="card-obsidian p-7 flex flex-col">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${t.grad} flex items-center justify-center mb-4`}>
                <Star className="w-7 h-7 text-white/90" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-navy">{t.name}</h3>
              <p className="text-xs text-slate-500 font-mono mb-2">Desde {t.pts}</p>
              <p className="text-sm text-slate-600 italic mb-4">{t.tagline}</p>
              <ul className="space-y-2 mt-auto">
                {t.perks.map((p, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Planes: Armony Contigo, Armony Contigo Plus y Armony Contigo Premium */}
      <section id="planes" className="bg-navy py-20 scroll-mt-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.2em] text-brand-salmon font-semibold">Lleva tu bienestar más lejos</span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mt-3">Elige tu plan Armony</h2>
            <p className="text-sm text-white/70 mt-2 max-w-2xl mx-auto">
              Empieza gratis con el programa de fidelización y súmale salud mental, autogestión y atención profesional cuando quieras.
            </p>
            <div className="mt-6 inline-flex items-center rounded-full bg-white/10 p-1" data-testid="landing-billing-toggle">
              <button
                data-testid="landing-billing-month"
                onClick={() => setBilling("month")}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${billing === "month" ? "bg-white text-navy" : "text-white/80 hover:text-white"}`}
              >
                Mensual
              </button>
              <button
                data-testid="landing-billing-year"
                onClick={() => setBilling("year")}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${billing === "year" ? "bg-white text-navy" : "text-white/80 hover:text-white"}`}
              >
                Anual <span className="text-brand-salmon font-semibold">· ahorra</span>
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {plans.map((plan: any) => {
              const isFree = !!plan.free;
              const price = isFree ? null : plan.prices[billing];
              return (
                <div
                  key={plan.id}
                  data-testid={`landing-plan-${plan.id}`}
                  className={`bg-white rounded-3xl p-7 flex flex-col relative overflow-hidden shadow-lg ${plan.highlight ? "ring-2 ring-gold md:scale-[1.03]" : ""}`}
                >
                  {plan.highlight && (
                    <span className="self-start mb-3 inline-flex items-center gap-1 text-xs font-semibold text-gold-ink bg-gold/10 px-3 py-1 rounded-full">
                      <Crown className="w-3.5 h-3.5" /> Recomendado
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    {isFree ? <Star className="w-5 h-5 text-navy" /> : plan.highlight ? <Crown className="w-5 h-5 text-gold" /> : <Sparkles className="w-5 h-5 text-navy" />}
                    <h3 className="text-xl font-serif font-semibold text-navy">{plan.name}</h3>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{plan.tagline}</p>

                  <div className="mt-5 flex items-baseline gap-1">
                    {isFree ? (
                      <span className="text-4xl font-bold text-navy">Gratis</span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-navy">${price.amount.toFixed(2)}</span>
                        <span className="text-slate-500 text-sm">/{billing === "year" ? "año" : "mes"}</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {isFree ? "Incluido con tu membresía Armony" : billing === "year" ? "Facturación anual en un solo pago" : "Facturación mensual"}
                  </p>

                  {plan.includes_note && (
                    <p className="mt-5 text-xs font-semibold text-navy uppercase tracking-wide">{plan.includes_note}</p>
                  )}
                  <ul className={`${plan.includes_note ? "mt-3" : "mt-6"} space-y-3 flex-1`}>
                    {plan.features.map((f: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    data-testid={`landing-plan-cta-${plan.id}`}
                    onClick={() => navigate("/registro")}
                    className={`mt-7 w-full rounded-full font-semibold ${isFree ? "bg-navy/5 text-navy hover:bg-navy/10" : plan.highlight ? "gold-gradient-bg text-white" : "bg-navy text-white hover:bg-navy/90"}`}
                  >
                    {isFree ? "Empezar gratis" : "Quiero este plan"}
                  </Button>
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-white/50 mt-6">
            Los planes Armony Contigo Plus y Premium se contratan desde tu perfil una vez que te registras. Pagos seguros con Stripe.
          </p>
        </div>
      </section>

      {/* Raffle */}
      <section id="sorteo" className="max-w-7xl mx-auto px-6 py-20 scroll-mt-16">
        <div className="rounded-3xl overflow-hidden gold-gradient-bg brand-waves text-white grid lg:grid-cols-2 items-center">
          <div className="p-10 sm:p-14">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-white/80">Armony equipa 5 hogares</span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold mt-3 leading-tight">
              Por cada 150 puntos, un boleto para el gran sorteo del 21 de diciembre
            </h2>
            <p className="mt-4 text-white/90 leading-relaxed">
              El primer premio: equipamiento completo del hogar con electrodomésticos de línea blanca y electrónica. Mientras más acumulas, más oportunidades tienes de ganar.
            </p>
            <Button data-testid="raffle-register-button" onClick={() => navigate("/registro")} className="mt-7 bg-white text-navy hover:bg-white/90 font-semibold rounded-full px-7 h-12">
              ¡Acumula hoy! <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="h-full min-h-[280px]">
            <img src={RAFFLE_IMG} alt="Hogar equipado" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Catalog preview */}
      <section className="bg-secondary/60 py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-xs uppercase tracking-[0.2em] text-gold-ink font-semibold">Convierte tus puntos en beneficios reales</span>
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy mt-3 mb-10">Canjea gift cards en lo que ya disfrutas</h2>
          <div className="flex flex-wrap justify-center gap-5">
            {giftcards.map((g, i) => (
              <div key={i} className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 flex flex-col items-center gap-3 w-40">
                <div className="w-12 h-12 rounded-full bg-navy/5 flex items-center justify-center">
                  <g.icon className="w-6 h-6 text-navy" />
                </div>
                <span className="text-sm font-medium text-slate-700">{g.label}</span>
              </div>
            ))}
          </div>
          <Button data-testid="catalog-register-button" onClick={() => navigate("/registro")} className="mt-10 bg-gold hover:bg-gold-light text-navy-dark font-bold rounded-full px-8 h-12">
            Quiero mis beneficios
          </Button>
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h2 className="text-3xl sm:text-4xl font-serif font-bold text-navy">¿Listo para activar tu membresía?</h2>
        <p className="mt-4 text-slate-600">Regístrate con tu número de cédula y empieza a acumular puntos hoy mismo.</p>
        <Button data-testid="final-register-button" onClick={() => navigate("/registro")} className="mt-7 bg-gold hover:bg-gold-light text-navy-dark font-bold rounded-full px-8 h-12 gold-glow">
          Registrarme ahora <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </section>

      {/* Footer */}
      <footer className="bg-navy-dark text-white/80 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <BrandLogo bare imgClassName="h-7" />
          <p className="text-xs text-white/60">© 2026 Armony Ecuador · Armony Contigo. Tu lealtad, tus beneficios. Tu tranquilidad, nuestro compromiso.</p>
        </div>
      </footer>
    </div>
  );
}
